import React, { type PropsWithChildren } from "react";
import { overrideTailwindClasses } from "tailwind-override";

type RtrButtonProps = PropsWithChildren<{
  className?: string;
  disabled?: boolean;
  loading?: boolean;
  onClick?: () => void;
  href?: string | null;
  style?: "primary" | "secondary" | "tertiary";
  fullWidth?: boolean;
  type?: "submit" | "button" | "reset";
  role?: string;
  dataHeapId?: string;
  dataTestId?: string;
}>;

const RtrButton = ({
  className = "",
  disabled = false,
  loading = false,
  onClick = () => {},
  href = null,
  style = "primary",
  fullWidth = false,
  type = "submit",
  role,
  dataHeapId,
  dataTestId,
  children,
}: RtrButtonProps) => {
  const baseStyles =
    "rounded-3xl px-4 h-11 leading-none capitalize active:opacity-60 flex items-center justify-center gap-2";

  const styles = {
    primary: { default: "bg-wine text-white", disabled: "bg-darkerGray text-white disabled:opacity-100" },
    secondary: {
      default: "bg-white text-wine border border-wine border-solid",
      disabled: "bg-white text-darkerGray border border-darkerGray disabled:opacity-100",
    },
    tertiary: {
      default: "bg-transparent text-current border border-current",
      disabled: "bg-transparent text-darkerGray border border-darkerGray disabled:opacity-100",
    },
  };

  const state = disabled || loading ? "disabled" : "default";

  const size = fullWidth ? "w-full" : "w-36";

  const classNames = overrideTailwindClasses(`${baseStyles} ${styles[style][state]} ${size} ${className}`);

  const loadingSpinner = () => (
    <div
      data-test-id="loading-spinner"
      className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
  );

  if (href) {
    return (
      <a className={classNames} href={href} role={role} data-heap-id={dataHeapId} data-test-id={dataTestId}>
        {children}
      </a>
    );
  }

  return (
    <button
      className={classNames}
      onClick={onClick}
      disabled={disabled || loading}
      type={type}
      role={role}
      data-heap-id={dataHeapId}
      data-test-id={dataTestId}>
      {children}
      {loading && loadingSpinner()}
    </button>
  );
};

export default RtrButton;
